import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DollarSign, Upload, Calculator, Lock, Unlock, History, AlertTriangle,
  Loader2, FileSpreadsheet, ChevronDown, ChevronUp, CheckCircle2, Save, X,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/utils/api';

const AREAS_OP = ['Contabilidad', 'Tributaria', 'Legal'];
const AREAS_SOP = ['Comercial', 'Adm. y Finanzas'];
const AREAS_ALL = [...AREAS_OP, ...AREAS_SOP];

const fmt = (n) => {
  if (n === undefined || n === null || isNaN(n)) return '$0';
  return '$' + Math.round(Number(n)).toLocaleString('es-CL');
};
const fmtPct = (n) => ((Number(n) || 0) * 100).toFixed(2) + '%';

// Generar últimos 12 meses
const buildPeriodos = () => {
  const out = [];
  const now = new Date();
  for (let i = -1; i < 11; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const p = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    out.push({ value: p, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return out;
};

export default function ComisionesLideresTab() {
  const periodos = buildPeriodos();
  const [periodo, setPeriodo] = useState(periodos[1].value); // mes anterior por defecto
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [auditoriaOpen, setAuditoriaOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comisiones/calcular/${periodo}`);
      setData(data);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => { load(); }, [load]);

  const puedeEditar = data?.puede_editar;
  const cierre = data?.cierre;

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Comisiones Líderes</h2>
              <p className="text-xs text-slate-500">
                Motor automatizado de costos, exigencia 40% y comisiones variables por área.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-600">Período producción:</Label>
            <select
              value={periodo}
              onChange={e => setPeriodo(e.target.value)}
              className="h-9 px-3 border border-slate-300 rounded bg-white text-sm"
              data-testid="comisiones-periodo"
            >
              {periodos.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUploadOpen(true)}
              disabled={!puedeEditar}
              data-testid="comisiones-upload-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1" /> Cargar sueldos (Excel)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAuditoriaOpen(true)}
              data-testid="comisiones-auditoria-btn"
            >
              <History className="w-3.5 h-3.5 mr-1" /> Auditoría
            </Button>
            <Button
              size="sm"
              onClick={load}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={loading}
              data-testid="comisiones-recalcular"
            >
              <Calculator className="w-3.5 h-3.5 mr-1" /> {loading ? 'Calculando...' : 'Recalcular'}
            </Button>
          </div>
        </div>
        {!puedeEditar && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" />
            Modo solo-lectura. Únicamente cmoreno, jcarrasco y dliempe pueden editar valores manuales.
          </div>
        )}
        {cierre && (
          <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Período cerrado por {cierre.cerrado_por} el {new Date(cierre.cerrado_at).toLocaleString('es-CL')}.
          </div>
        )}
      </Card>

      {loading && !data ? (
        <Card className="p-12 flex flex-col items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          <p className="text-sm text-slate-500 mt-3">Cargando cálculo...</p>
        </Card>
      ) : data ? (
        <>
          {data.costos.remuneraciones_cargadas === 0 && (
            <Card className="p-3 border-amber-300 bg-amber-50">
              <div className="flex items-center gap-2 text-amber-900 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <strong>No hay sueldos cargados para este período.</strong>
                <span className="text-xs">Carga el Excel de remuneraciones para ver el cálculo completo.</span>
              </div>
            </Card>
          )}
          <Seccion1Costos data={data} periodo={periodo} onChange={load} puedeEditar={puedeEditar} />
          <Seccion2Cascada data={data} />
          <Seccion2bRecaudacionSoporte data={data} periodo={periodo} onChange={load} puedeEditar={puedeEditar} />
          <Seccion3Comisiones data={data} />
          <BotonCerrarMes data={data} periodo={periodo} puedeEditar={puedeEditar} onChange={load} />
        </>
      ) : null}

      {uploadOpen && (
        <UploadExcelDialog
          periodo={periodo}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => { setUploadOpen(false); load(); }}
        />
      )}
      {auditoriaOpen && (
        <AuditoriaDialog periodo={periodo} onClose={() => setAuditoriaOpen(false)} />
      )}
    </div>
  );
}

// ============================================================
// SECCIÓN 1: COSTOS DIRECTOS Y PROVISIONES
// ============================================================
const Seccion1Costos = ({ data, periodo, onChange, puedeEditar }) => {
  const costos = data.costos;
  return (
    <Card className="p-0 border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <h3 className="text-sm font-bold text-slate-800">1. Costos Directos y Provisiones Mensuales</h3>
        <p className="text-[11px] text-slate-500">
          Sueldo socios total: <strong>{fmt(costos.sueldo_socios_total)}</strong> · Headcount no-socios: <strong>{costos.headcount_total}</strong> · Sueldos cargados: <strong>{costos.remuneraciones_cargadas}</strong>
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Concepto</th>
              {AREAS_ALL.map(a => (
                <th key={a} className="text-right px-3 py-2 font-semibold whitespace-nowrap">{a}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <FilaCosto label="Gastos Directos + Prorrateo" field="gastos_directos" data={data} periodo={periodo} onChange={onChange} puedeEditar={puedeEditar} />
            <FilaCosto label="Remuneración Real (no socios)" field="remuneracion_real" data={data} periodo={periodo} onChange={onChange} puedeEditar={puedeEditar} />
            <FilaCosto label="Sueldo Socios (÷ 5)" field="sueldo_socios" data={data} periodo={periodo} onChange={onChange} puedeEditar={puedeEditar} />
            <FilaCosto label="Provisión Bono (Bruto÷12)" field="provision_bono" data={data} periodo={periodo} onChange={onChange} puedeEditar={puedeEditar} />
            <FilaCosto label="Provisión Aguinaldos ($20.833/empleado)" field="provision_aguinaldo" data={data} periodo={periodo} onChange={onChange} puedeEditar={puedeEditar} />
            <tr className="bg-indigo-50 border-t-2 border-indigo-200 font-bold">
              <td className="px-3 py-2 text-slate-800">Costo por Área (Base)</td>
              {AREAS_ALL.map(a => (
                <td key={a} className="text-right px-3 py-2 tabular-nums text-indigo-900">{fmt(costos.areas[a]?.total)}</td>
              ))}
            </tr>
            <tr className="text-[11px] text-slate-500 italic">
              <td className="px-3 py-1">Headcount</td>
              {AREAS_ALL.map(a => (
                <td key={a} className="text-right px-3 py-1">{costos.areas[a]?.headcount || 0}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const FilaCosto = ({ label, field, data, periodo, onChange, puedeEditar }) => {
  const [editing, setEditing] = useState(null);
  const [val, setVal] = useState('');

  const startEdit = (area) => {
    if (!puedeEditar) return;
    setEditing(area);
    setVal(String(Math.round(data.costos.areas[area][field] || 0)));
  };

  const save = async (area) => {
    const num = Number(val.replace(/\D/g, ''));
    try {
      await api.post('/comisiones/override', {
        periodo,
        path: `costos.areas.${area}.${field}`,
        valor: num,
        motivo: `Override manual ${field}`,
      });
      toast.success('Override aplicado');
      setEditing(null);
      onChange();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-3 py-1.5 text-slate-700">{label}</td>
      {AREAS_ALL.map(a => {
        const v = data.costos.areas[a]?.[field] || 0;
        return (
          <td key={a} className="text-right px-3 py-1.5 tabular-nums">
            {editing === a ? (
              <span className="inline-flex items-center gap-1">
                <Input
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  className="h-6 w-24 text-xs text-right inline-block"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && save(a)}
                />
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => save(a)}><Save className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditing(null)}><X className="w-3 h-3" /></Button>
              </span>
            ) : (
              <span
                onClick={() => startEdit(a)}
                className={puedeEditar ? 'cursor-pointer hover:bg-amber-100 px-1 rounded' : ''}
                title={puedeEditar ? 'Click para editar' : 'Solo lectura'}
              >
                {fmt(v)}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
};

// ============================================================
// SECCIÓN 2: REDISTRIBUCIÓN Y EXIGENCIA
// ============================================================
const Seccion2Cascada = ({ data }) => {
  const c = data.cascada;
  const e = data.exigencia;
  return (
    <Card className="p-0 border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <h3 className="text-sm font-bold text-slate-800">2. Redistribución de Soporte y Exigencia (40%)</h3>
        <p className="text-[11px] text-slate-500">
          Pozo Soporte (Comercial + Adm.y Finanzas): <strong>{fmt(c.pozo_soporte)}</strong> ·
          GTOTOT: <strong>{fmt(e.gtotot)}</strong> ·
          Exigencia Global: <strong>{fmt(e.exigencia_global)}</strong>
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Concepto</th>
              {AREAS_OP.map(a => (
                <th key={a} className="text-right px-3 py-2 font-semibold whitespace-nowrap">{a}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-700">Headcount operativo</td>
              {AREAS_OP.map(a => (<td key={a} className="text-right px-3 py-1.5 tabular-nums">{c.headcount_operativos[a] || 0}</td>))}
            </tr>
            <tr className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-700">Redistribución desde Soporte</td>
              {AREAS_OP.map(a => (<td key={a} className="text-right px-3 py-1.5 tabular-nums text-indigo-700">{fmt(c.redistribuido[a])}</td>))}
            </tr>
            <tr className="bg-indigo-50 border-t-2 border-indigo-200 font-semibold">
              <td className="px-3 py-1.5 text-slate-800">Costo C/Redistr.</td>
              {AREAS_OP.map(a => (<td key={a} className="text-right px-3 py-1.5 tabular-nums">{fmt(c.costo_con_redistr[a])}</td>))}
            </tr>
            <tr className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-700">Recaudación Operativa</td>
              {AREAS_OP.map(a => (<td key={a} className="text-right px-3 py-1.5 tabular-nums">{fmt(e.recaudacion_por_area[a])}</td>))}
            </tr>
            <tr className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-700">% sobre recaudación operativa</td>
              {AREAS_OP.map(a => (<td key={a} className="text-right px-3 py-1.5 tabular-nums">{fmtPct(e.pct_recaudacion_op[a])}</td>))}
            </tr>
            <tr className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-700">Exigencia ponderada (40%)</td>
              {AREAS_OP.map(a => (<td key={a} className="text-right px-3 py-1.5 tabular-nums text-violet-700">{fmt(e.exigencia_area[a])}</td>))}
            </tr>
            <tr className="bg-violet-50 border-t-2 border-violet-200 font-bold">
              <td className="px-3 py-1.5 text-slate-800">Costo Total con Exigencia</td>
              {AREAS_OP.map(a => (<td key={a} className="text-right px-3 py-1.5 tabular-nums text-violet-900">{fmt(data.costo_total_con_exigencia[a])}</td>))}
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ============================================================
// SECCIÓN 2b: RECAUDACIÓN ÁREAS DE SOPORTE (Editable manualmente)
// ============================================================
const Seccion2bRecaudacionSoporte = ({ data, periodo, onChange, puedeEditar }) => {
  const [activos, setActivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const e = data.exigencia;

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/comisiones/recaudacion-overrides/${periodo}`);
      setActivos(r.data?.activos || []);
    } catch { /* ignore */ }
  }, [periodo]);
  useEffect(() => { refresh(); }, [refresh]);

  const overrideFor = (area) => activos.find(o => o.area === area);

  const guardar = async (area, valor, motivo) => {
    setLoading(true);
    try {
      await api.post('/comisiones/recaudacion-override', {
        periodo, area, valor: Number(valor) || 0, motivo: motivo || null,
      });
      toast.success(`Recaudación de ${area} actualizada`);
      await refresh();
      onChange();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally { setLoading(false); }
  };

  const limpiar = async (area) => {
    if (!window.confirm(`¿Quitar el override y volver al valor calculado para ${area}?`)) return;
    setLoading(true);
    try {
      await api.delete('/comisiones/recaudacion-override', {
        params: { periodo, area },
      });
      toast.success('Override eliminado');
      await refresh();
      onChange();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <Card className="p-0 border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800">2b. Recaudación Áreas de Soporte</h3>
          <p className="text-[11px] text-slate-500">
            Editable manualmente por el equipo autorizado (Comercial y Adm. y Finanzas no provienen de facturas directas).
          </p>
        </div>
        {!puedeEditar && (
          <Badge className="bg-slate-200 text-slate-700">Solo lectura</Badge>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {AREAS_SOP.map(area => {
          const valor = e.recaudacion_por_area?.[area] || 0;
          const ov = overrideFor(area);
          return (
            <FilaRecaudacionSop
              key={area}
              area={area}
              valor={valor}
              override={ov}
              puedeEditar={puedeEditar}
              loading={loading}
              onGuardar={(v, m) => guardar(area, v, m)}
              onLimpiar={() => limpiar(area)}
            />
          );
        })}
      </div>
    </Card>
  );
};

const FilaRecaudacionSop = ({ area, valor, override, puedeEditar, loading, onGuardar, onLimpiar }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [motivo, setMotivo] = useState('');

  const start = () => {
    if (!puedeEditar) return;
    setVal(String(Math.round(valor || 0)));
    setMotivo(override?.motivo || '');
    setEditing(true);
  };

  return (
    <div className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`recaudacion-sop-${area}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-semibold text-slate-800">{area}</div>
        {override ? (
          <div className="text-[11px] text-amber-700 mt-0.5">
            Editado manualmente · {override.user_name} ·{' '}
            {new Date(override.timestamp).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
            {override.motivo ? ` · "${override.motivo}"` : ''}
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 mt-0.5">Valor calculado desde cobranzas (0 por defecto).</div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {editing ? (
          <>
            <Input
              value={val}
              onChange={(ev) => setVal(ev.target.value.replace(/[^\d]/g, ''))}
              className="h-8 w-32 text-right tabular-nums"
              autoFocus
              data-testid={`recaudacion-sop-input-${area}`}
            />
            <Input
              value={motivo}
              onChange={(ev) => setMotivo(ev.target.value)}
              placeholder="Motivo (opcional)"
              className="h-8 w-44"
            />
            <Button size="sm" onClick={() => { onGuardar(val, motivo); setEditing(false); }} disabled={loading} className="h-8 bg-indigo-600 hover:bg-indigo-700" data-testid={`recaudacion-sop-save-${area}`}>
              <Save className="w-3.5 h-3.5 mr-1" /> Guardar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={loading} className="h-8">
              <X className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <div className="text-base font-bold tabular-nums text-violet-900 min-w-[120px] text-right">
              {fmt(valor)}
            </div>
            {puedeEditar && (
              <Button size="sm" variant="outline" onClick={start} className="h-8" data-testid={`recaudacion-sop-edit-${area}`}>
                Editar
              </Button>
            )}
            {puedeEditar && override && (
              <Button size="sm" variant="ghost" onClick={onLimpiar} className="h-8 text-rose-600 hover:text-rose-700" data-testid={`recaudacion-sop-clear-${area}`}>
                Quitar override
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};


// ============================================================
// SECCIÓN 3: RESUMEN DE COMISIONES
// ============================================================
const Seccion3Comisiones = ({ data }) => {
  const co = data.comisiones_operativas;
  const cc = data.comision_comercial;
  const cf = data.comision_finanzas;
  return (
    <Card className="p-0 border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <h3 className="text-sm font-bold text-slate-800">3. Resumen Ejecutivo de Comisiones por Líder</h3>
        <p className="text-[11px] text-slate-500">
          Período evaluado: <strong>{data.periodo_evaluacion_comisiones}</strong> · RAO Global: <strong className={co.rao_global > 0 ? 'text-emerald-700' : 'text-rose-700'}>{fmt(co.rao_global)}</strong> · Pozo Comisión: <strong>{fmt(co.monto_comision_global)}</strong>
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-3 py-2">Área / Líder</th>
              <th className="text-right px-3 py-2">Recaudación</th>
              <th className="text-right px-3 py-2">Costo Total c/Exig.</th>
              <th className="text-right px-3 py-2">Rentabilidad</th>
              <th className="text-right px-3 py-2">Com. Oficina (20%)</th>
              <th className="text-right px-3 py-2">Com. Área (80%)</th>
              <th className="text-right px-3 py-2">Ajuste Mín.</th>
              <th className="text-right px-3 py-2 bg-emerald-100">Bruto Total</th>
            </tr>
          </thead>
          <tbody>
            {AREAS_OP.map(a => {
              const r = co.resumen[a] || {};
              return (
                <tr key={a} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`comision-row-${a}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800">{a}</div>
                    <div className="text-[10px] text-slate-500">{r.lider_nombre || '—'} · {r.lider_email}</div>
                  </td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(r.recaudacion)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(r.costo_total_con_exigencia)}</td>
                  <td className={`text-right px-3 py-2 tabular-nums font-semibold ${r.rentabilidad >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(r.rentabilidad)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(r.comision_oficina)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(r.comision_area)}</td>
                  <td className="text-right px-3 py-2 tabular-nums text-amber-700">{fmt(r.ajuste_minimo_garantizado)}</td>
                  <td className="text-right px-3 py-2 tabular-nums font-bold bg-emerald-50 text-emerald-900">{fmt(r.bruto_total)}</td>
                </tr>
              );
            })}
            {/* Comercial */}
            <tr className="border-t-2 border-slate-200 hover:bg-slate-50" data-testid="comision-row-Comercial">
              <td className="px-3 py-2">
                <div className="font-semibold text-slate-800">Comercial</div>
                <div className="text-[10px] text-slate-500">{cc.lider_nombre || '—'} · {cc.lider_email}</div>
                <div className="text-[10px] text-violet-600">Atribución Meta Ads · ciclo 26→25</div>
              </td>
              <td className="text-right px-3 py-2 tabular-nums" colSpan={3}>
                Recaudación Meta: <strong>{fmt(cc.recaudacion_meta_ads)}</strong> ({cc.facturas_atribuidas} facturas)
              </td>
              <td className="text-right px-3 py-2 tabular-nums">5% bruto: {fmt(cc.bruto_5pct)}</td>
              <td className="text-right px-3 py-2 tabular-nums">Neta (×0.875): {fmt(cc.comision_neta)}</td>
              <td className="text-right px-3 py-2 tabular-nums text-slate-400">—</td>
              <td className="text-right px-3 py-2 tabular-nums font-bold bg-emerald-50 text-emerald-900">{fmt(cc.bruto_total)}</td>
            </tr>
            {/* Finanzas */}
            <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid="comision-row-Finanzas">
              <td className="px-3 py-2">
                <div className="font-semibold text-slate-800">Adm. y Finanzas</div>
                <div className="text-[10px] text-slate-500">{cf.lider_nombre || '—'} · {cf.lider_email}</div>
                <div className="text-[10px] text-violet-600">Cobranza pesada {'>'} 90 días</div>
              </td>
              <td className="text-right px-3 py-2 tabular-nums" colSpan={3}>
                Recaudación atrasada: <strong>{fmt(cf.recaudacion_atrasada_90d)}</strong> ({cf.facturas_atrasadas} facturas)
              </td>
              <td className="text-right px-3 py-2 tabular-nums">5% bruto: {fmt(cf.bruto_5pct)}</td>
              <td className="text-right px-3 py-2 tabular-nums">Variable: {fmt(cf.comision_variable_neta)}</td>
              <td className="text-right px-3 py-2 tabular-nums text-amber-700">{fmt(cf.ajuste_minimo_garantizado)}</td>
              <td className="text-right px-3 py-2 tabular-nums font-bold bg-emerald-50 text-emerald-900">{fmt(cf.bruto_total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ============================================================
// CERRAR MES
// ============================================================
const BotonCerrarMes = ({ data, periodo, puedeEditar, onChange }) => {
  const [loading, setLoading] = useState(false);
  const cerrado = data.cierre;

  const cerrar = async () => {
    if (!window.confirm(`¿Cerrar las comisiones del período ${periodo}? Quedará congelado.`)) return;
    setLoading(true);
    try {
      await api.post(`/comisiones/cerrar/${periodo}`);
      toast.success('Período cerrado. La inyección a liquidaciones queda pendiente para próxima fase.');
      onChange();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-4 flex items-center justify-between border-slate-200">
      <div className="text-xs text-slate-600">
        <strong>Inyección a liquidaciones de sueldo:</strong> pendiente Fase B (cuando exista módulo de Remuneraciones formal).
      </div>
      <Button
        onClick={cerrar}
        disabled={!puedeEditar || loading || !!cerrado}
        className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
        data-testid="comisiones-cerrar-btn"
      >
        {cerrado ? <Lock className="w-4 h-4 mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
        {cerrado ? 'Período cerrado' : (loading ? 'Cerrando...' : 'Cerrar Comisión del Mes')}
      </Button>
    </Card>
  );
};

// ============================================================
// UPLOAD EXCEL DIALOG
// ============================================================
const UploadExcelDialog = ({ periodo, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const submit = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('periodo', periodo);
    fd.append('file', file);
    setLoading(true);
    try {
      const { data } = await api.post('/comisiones/upload-remuneraciones', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      toast.success(`${data.procesadas} sueldos cargados`);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="comisiones-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Cargar Sueldos · Período {periodo}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700">
            <p className="font-semibold mb-1">Formato del Excel:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Columna <code>email</code>: correo corporativo del funcionario.</li>
              <li>Columna <code>sueldo_bruto</code> (o <code>costo</code>): costo empresa total.</li>
              <li>Columna <code>nombre</code> (opcional).</li>
              <li>Los socios (cargo="Socio") se prorratean entre las 5 áreas.</li>
            </ul>
          </div>
          <Input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0])}
            data-testid="comisiones-upload-input"
          />
          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs space-y-1">
              <p className="text-emerald-800 font-semibold">✓ {result.procesadas} sueldos cargados de {result.total_filas} filas</p>
              {result.invalidas?.length > 0 && (
                <div className="text-amber-800">
                  <p className="font-semibold">{result.invalidas.length} filas con errores:</p>
                  <ul className="list-disc list-inside text-[11px] max-h-20 overflow-y-auto">
                    {result.invalidas.slice(0, 10).map((i, idx) => (
                      <li key={idx}>Fila {i.fila}: {i.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button
            onClick={result ? onSuccess : submit}
            disabled={loading || !file}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="comisiones-upload-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {result ? 'Aplicar' : 'Subir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// AUDITORÍA
// ============================================================
const AuditoriaDialog = ({ periodo, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/comisiones/auditoria/${periodo}`);
        setRows(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [periodo]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="comisiones-auditoria-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-600" />
            Log de Overrides · {periodo}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 italic text-center py-4">Sin ediciones manuales registradas.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-2 py-1.5">Fecha</th>
                <th className="text-left px-2 py-1.5">Usuario</th>
                <th className="text-left px-2 py-1.5">Campo</th>
                <th className="text-right px-2 py-1.5">Antes</th>
                <th className="text-right px-2 py-1.5">Después</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{new Date(r.timestamp).toLocaleString('es-CL')}</td>
                  <td className="px-2 py-1.5">{r.user_email}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{r.path}</td>
                  <td className="text-right px-2 py-1.5 tabular-nums">{fmt(r.old_value)}</td>
                  <td className="text-right px-2 py-1.5 tabular-nums">{fmt(r.new_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
